import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, CheckCircle2, Clock, AlertTriangle, Circle, Zap,
  ChevronDown, ChevronRight, Target, Flag, Activity
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

function getStatusBadgeClass(status) {
  const styles = {
    completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400',
    in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400',
  };
  return styles[status] || 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400';
}

function getMilestoneCircleClass(status, isDark) {
  const styles = {
    completed: 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30',
    in_progress: 'border-blue-500 bg-blue-50 dark:bg-blue-900/30',
  };
  return styles[status] || (isDark ? 'border-slate-500 bg-slate-700' : 'border-slate-300 bg-white');
}

function StatusIcon({ status, size = 16 }) {
  if (status === 'completed') return <CheckCircle2 size={size} className="text-emerald-500" />;
  if (status === 'in_progress') return <Clock size={size} className="text-blue-500" />;
  return <Circle size={size} className="text-slate-400 dark:text-slate-500" />;
}

/* ========== MILESTONE TIMELINE ========== */
function MilestoneTimeline({ milestones, isDark }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="milestone-timeline">
      <h3 className="text-sm font-bold text-slate-800 mb-4 uppercase tracking-wider" style={{ fontFamily: 'Outfit' }}>
        Investigation Milestones
      </h3>
      <div className="relative">
        <div className={`absolute left-[15px] top-3 bottom-3 w-0.5 ${isDark ? 'bg-slate-600' : 'bg-slate-200'}`} />
        <div className="space-y-1">
          {milestones.map((m) => (
            <div key={m.label} className="flex items-start gap-4 relative" data-testid={`milestone-${m.label.replace(/\s+/g, '-').toLowerCase()}`}>
              <div className={`relative z-10 shrink-0 w-[31px] h-[31px] rounded-full flex items-center justify-center border-2 ${getMilestoneCircleClass(m.status, isDark)}`}>
                <StatusIcon status={m.status} size={14} />
              </div>
              <div className="flex-1 pb-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-slate-800">{m.label}</span>
                  {m.type === 'phase' && (
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${getStatusBadgeClass(m.status)}`}>
                      {m.details}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-0.5">
                  {m.date && <span className="text-xs text-slate-400">{m.date.split('T')[0]}</span>}
                  {m.type !== 'phase' && m.details && <span className="text-xs text-slate-500">{m.details}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ========== GANTT CHART ========== */
function GanttChart({ modules, timelineStart, timelineEnd, isDark }) {
  const [expanded, setExpanded] = useState({});

  const STATUS_BAR_COLORS = {
    completed: isDark ? '#34d399' : '#10b981',
    in_progress: isDark ? '#60a5fa' : '#3b82f6',
    pending: isDark ? '#475569' : '#cbd5e1',
  };

  const PRIORITY_COLORS = {
    critical: '#dc2626',
    high: '#f97316',
    medium: isDark ? '#60a5fa' : '#3b82f6',
    low: '#22c55e',
  };

  if (!timelineStart || !timelineEnd) {
    return <div className="text-center py-8 text-slate-400">No task dates available for Gantt chart.</div>;
  }

  const startDate = new Date(timelineStart);
  const endDate = new Date(timelineEnd);
  const totalDays = Math.max(Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)), 1);

  const getPosition = (dateStr) => {
    if (!dateStr) return 0;
    const d = new Date(dateStr.split('T')[0]);
    const days = Math.ceil((d - startDate) / (1000 * 60 * 60 * 24));
    return Math.max(0, Math.min(100, (days / totalDays) * 100));
  };

  const getWidth = (startStr, endStr) => {
    const s = getPosition(startStr);
    const e = getPosition(endStr);
    return Math.max(e - s, 2);
  };

  const monthMarkers = [];
  const current = new Date(startDate);
  current.setDate(1);
  while (current <= endDate) {
    const pos = getPosition(current.toISOString());
    if (pos >= 0 && pos <= 100) {
      monthMarkers.push({
        label: current.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        position: pos,
      });
    }
    current.setMonth(current.getMonth() + 1);
  }

  const today = new Date();
  const todayPos = getPosition(today.toISOString());
  const showToday = todayPos > 0 && todayPos < 100;

  const headerBg = isDark ? 'bg-slate-800/50' : 'bg-slate-50';
  const rowBorder = isDark ? 'border-slate-700' : 'border-slate-50';
  const hoverBg = isDark ? 'hover:bg-slate-700/30' : 'hover:bg-slate-50/50';
  const expandedBg = isDark ? 'bg-slate-800/30' : 'bg-slate-50/30';
  const labelBorderR = isDark ? 'border-slate-700' : 'border-slate-100';
  const monthBorder = isDark ? 'border-slate-600' : 'border-slate-200';

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="gantt-chart">
      <div className={`px-5 py-3 border-b border-slate-200 ${headerBg}`}>
        <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider" style={{ fontFamily: 'Outfit' }}>
          Task Gantt Chart
        </h3>
        <p className="text-[10px] text-slate-400 mt-0.5">
          {timelineStart.split('T')[0]} to {timelineEnd.split('T')[0]} ({totalDays} days)
        </p>
      </div>

      <div className="overflow-x-auto">
        <div style={{ minWidth: 700 }}>
          {/* Month header */}
          <div className={`relative h-7 border-b ${isDark ? 'border-slate-700 bg-slate-800/30' : 'border-slate-100 bg-slate-50/50'}`}>
            {monthMarkers.map((m) => (
              <div key={m.label} className={`absolute top-0 h-full border-l ${monthBorder} flex items-center`}
                style={{ left: `calc(220px + (100% - 220px) * ${m.position / 100})` }}>
                <span className="text-[10px] text-slate-400 font-medium pl-1">{m.label}</span>
              </div>
            ))}
          </div>

          {/* Modules and tasks */}
          {modules.map((mod) => (
            <div key={mod.name}>
              <div className={`flex items-center border-b ${rowBorder} ${hoverBg} cursor-pointer`}
                onClick={() => setExpanded(p => ({ ...p, [mod.name]: !p[mod.name] }))}
                data-testid={`gantt-module-${mod.name.replace(/\s+/g, '-').toLowerCase()}`}>
                <div className={`w-[220px] shrink-0 px-4 py-2 flex items-center gap-2 border-r ${labelBorderR}`}>
                  {expanded[mod.name] ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                  <span className="text-xs font-semibold text-slate-700 truncate">{mod.name}</span>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${getStatusBadgeClass(mod.status)}`}>
                    {mod.completed_count}/{mod.total_count}
                  </span>
                </div>
                <div className="flex-1 relative h-8 px-2">
                  {mod.start_date && mod.end_date && (
                    <div className="absolute top-1/2 -translate-y-1/2 h-5 rounded-md"
                      style={{
                        left: `${getPosition(mod.start_date)}%`,
                        width: `${getWidth(mod.start_date, mod.end_date)}%`,
                        background: `linear-gradient(90deg, ${STATUS_BAR_COLORS[mod.status] || STATUS_BAR_COLORS.pending}, ${STATUS_BAR_COLORS[mod.status] || STATUS_BAR_COLORS.pending}88)`,
                      }}>
                      {mod.total_count > 0 && (
                        <div className="h-full rounded-md" style={{ width: `${(mod.completed_count / mod.total_count) * 100}%`, backgroundColor: 'rgba(255,255,255,0.25)' }} />
                      )}
                    </div>
                  )}
                  {showToday && (
                    <div className="absolute top-0 bottom-0 w-0.5 bg-red-400 z-10" style={{ left: `${todayPos}%` }} />
                  )}
                </div>
              </div>

              {expanded[mod.name] && mod.tasks.map((task) => (
                <div key={task.id} className={`flex items-center border-b ${rowBorder} ${expandedBg}`}>
                  <div className={`w-[220px] shrink-0 pl-10 pr-4 py-1.5 flex items-center gap-2 border-r ${labelBorderR}`}>
                    <StatusIcon status={task.status} size={12} />
                    <span className="text-[11px] text-slate-600 truncate" title={task.title}>{task.title}</span>
                    {task.escalated && <Zap className="w-3 h-3 text-red-500 shrink-0" />}
                  </div>
                  <div className="flex-1 relative h-6 px-2">
                    {task.start_date && task.due_date && (
                      <div className="absolute top-1/2 -translate-y-1/2 h-3 rounded-sm"
                        style={{
                          left: `${getPosition(task.start_date)}%`,
                          width: `${getWidth(task.start_date, task.due_date)}%`,
                          backgroundColor: PRIORITY_COLORS[task.priority] || '#94a3b8',
                          opacity: task.status === 'completed' ? 0.5 : 0.85,
                        }}>
                        {task.progress_pct > 0 && (
                          <div className="h-full rounded-sm" style={{ width: `${task.progress_pct}%`, backgroundColor: 'rgba(255,255,255,0.35)' }} />
                        )}
                      </div>
                    )}
                    {showToday && (
                      <div className="absolute top-0 bottom-0 w-0.5 bg-red-400 z-10 opacity-30" style={{ left: `${todayPos}%` }} />
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className={`px-5 py-2 border-t ${isDark ? 'border-slate-700 bg-slate-800/30' : 'border-slate-100 bg-slate-50/50'} flex flex-wrap gap-4`}>
        {Object.entries(PRIORITY_COLORS).map(([p, c]) => (
          <div key={p} className="flex items-center gap-1.5">
            <div className="w-3 h-2 rounded-sm" style={{ backgroundColor: c }} />
            <span className="text-[10px] text-slate-500 capitalize">{p}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5 ml-4">
          <div className="w-0.5 h-3 bg-red-400" />
          <span className="text-[10px] text-slate-500">Today</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Zap className="w-3 h-3 text-red-500" />
          <span className="text-[10px] text-slate-500">Escalated</span>
        </div>
      </div>
    </div>
  );
}

/* ========== MAIN TIMELINE PAGE ========== */
export default function Timeline() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadTimeline = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/investigations/${id}/timeline`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      console.error('Failed to load timeline:', e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadTimeline(); }, [loadTimeline]);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${isDark ? 'border-violet-400' : 'border-blue-500'}`} />
    </div>
  );
  if (!data) return <div className="text-center py-12 text-slate-500">Timeline data not available.</div>;

  const { investigation, milestones, modules, summary, timeline_start, timeline_end } = data;

  const statCards = [
    { label: 'Total Tasks', value: summary.total_tasks, icon: Target, color: isDark ? 'text-slate-300' : 'text-slate-700' },
    { label: 'Completed', value: summary.completed, icon: CheckCircle2, color: 'text-emerald-500' },
    { label: 'In Progress', value: summary.in_progress, icon: Activity, color: 'text-blue-500' },
    { label: 'Pending', value: summary.pending, icon: Clock, color: isDark ? 'text-slate-400' : 'text-slate-400' },
    { label: 'Escalated', value: summary.escalated, icon: AlertTriangle, color: 'text-red-500' },
    { label: 'Completion', value: `${summary.completion_pct}%`, icon: Flag, color: 'text-violet-500' },
  ];

  const getProgressGradient = (pct) => {
    if (pct === 100) return 'linear-gradient(90deg, #10b981, #059669)';
    if (pct > 50) return 'linear-gradient(90deg, #7c3aed, #a855f7)';
    return 'linear-gradient(90deg, #f59e0b, #f97316)';
  };

  const progressGradient = getProgressGradient(summary.completion_pct);

  return (
    <div className="max-w-7xl mx-auto" data-testid="timeline-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/investigations/${id}`)} data-testid="timeline-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Investigation Timeline
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
              {investigation.number} ({investigation.sector?.toUpperCase()})
            </p>
          </div>
        </div>
        <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${getStatusBadgeClass(
          ['completed', 'closed'].includes(investigation.status) ? 'completed' :
          investigation.status === 'in_progress' ? 'in_progress' : 'pending'
        )}`}>
          {(investigation.status || '').replace('_', ' ').toUpperCase()}
        </span>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 sm:gap-3 mb-6" data-testid="timeline-summary">
        {statCards.map((stat) => (
          <div key={stat.label} className="bg-white rounded-lg border border-slate-200 p-3 flex items-center gap-3">
            <stat.icon className={`w-5 h-5 ${stat.color} shrink-0`} />
            <div>
              <div className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>{stat.value}</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider">{stat.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Overall progress bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6" data-testid="overall-progress">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-slate-700">Overall Progress</span>
          <span className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>{summary.completion_pct}%</span>
        </div>
        <div className={`h-3 rounded-full overflow-hidden ${isDark ? 'bg-slate-700' : 'bg-slate-100'}`}>
          <div className="h-full rounded-full transition-all duration-500"
            style={{ width: `${summary.completion_pct}%`, background: progressGradient }} />
        </div>
        <div className="flex items-center gap-4 mt-2">
          <span className="flex items-center gap-1 text-[10px] text-emerald-500">
            <span className="w-2 h-2 rounded-full bg-emerald-500" /> {summary.completed} completed
          </span>
          <span className="flex items-center gap-1 text-[10px] text-blue-500">
            <span className="w-2 h-2 rounded-full bg-blue-500" /> {summary.in_progress} in progress
          </span>
          <span className="flex items-center gap-1 text-[10px] text-slate-400">
            <span className="w-2 h-2 rounded-full bg-slate-300 dark:bg-slate-500" /> {summary.pending} pending
          </span>
        </div>
      </div>

      {/* Two-column layout: Milestones + Gantt */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-1">
          <MilestoneTimeline milestones={milestones} isDark={isDark} />
        </div>
        <div className="lg:col-span-2">
          <GanttChart modules={modules} timelineStart={timeline_start} timelineEnd={timeline_end} isDark={isDark} />
        </div>
      </div>

      {/* Module cards grid */}
      <div className="mb-6">
        <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-3" style={{ fontFamily: 'Outfit' }}>
          Specialist Module Progress
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="module-cards">
          {modules.map((mod) => {
            const pct = mod.total_count > 0 ? Math.round((mod.completed_count / mod.total_count) * 100) : 0;
            const cardStyles = {
              completed: isDark ? 'bg-emerald-900/20 border-emerald-800' : 'bg-emerald-50 border-emerald-200',
              in_progress: isDark ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200',
              pending: isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200',
            };
            const barColor = {
              completed: isDark ? '#34d399' : '#10b981',
              in_progress: isDark ? '#60a5fa' : '#3b82f6',
              pending: isDark ? '#475569' : '#cbd5e1',
            };
            return (
              <div key={mod.name} className={`rounded-xl border p-4 ${cardStyles[mod.status] || cardStyles.pending}`}
                data-testid={`module-card-${mod.name.replace(/\s+/g, '-').toLowerCase()}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-slate-800 truncate" title={mod.name}>{mod.name}</span>
                  <StatusIcon status={mod.status} size={16} />
                </div>
                <div className={`h-1.5 rounded-full overflow-hidden mb-2 ${isDark ? 'bg-slate-700' : 'bg-white/50'}`}>
                  <div className="h-full rounded-full transition-all"
                    style={{ width: `${pct}%`, backgroundColor: barColor[mod.status] || barColor.pending }} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-slate-500">{mod.completed_count}/{mod.total_count} tasks</span>
                  <span className="text-xs font-bold" style={{ color: barColor[mod.status] || '#94a3b8' }}>{pct}%</span>
                </div>
                {mod.start_date && (
                  <div className="text-[9px] text-slate-400 mt-1">
                    {mod.start_date.split('T')[0]} — {mod.end_date?.split('T')[0] || ''}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
